import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import snippets from '../../data/snippetsData';
import CopyButton from '../partials/CopyButton';

const SnippetPage = () => {
  const { id } = useParams();
  const snippet = snippets.find(s => s.id === id);
  const [activeVariant, setActiveVariant] = useState(0);

  useEffect(() => {
    if (snippet) document.title = `${snippet.title} — Snippets`;
    else document.title = 'Snippet not found';
  }, [snippet]);

  if (!snippet) {
    return (
      <Container>
        <BackLink to="/snippets">← Snippets</BackLink>
        <NotFound>Snippet not found.</NotFound>
      </Container>
    );
  }

  const current = snippet.variants[activeVariant];

  return (
    <Container>
      <BackLink to="/snippets">← Snippets</BackLink>

      <Header>
        <Title>{snippet.title}</Title>
        <Description>{snippet.description}</Description>
        <Tags>
          {snippet.tags.map(tag => <Tag key={tag}>{tag}</Tag>)}
        </Tags>
      </Header>

      <CodeBlock>
        <CodeHeader>
          <Tabs>
            {snippet.variants.map((v, i) => (
              <Tab key={v.lang} $active={i === activeVariant} onClick={() => setActiveVariant(i)}>
                {v.label}
              </Tab>
            ))}
          </Tabs>
          <CopyButton text={current.code} />
        </CodeHeader>
        <SyntaxHighlighter
          language={current.lang}
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            borderRadius: '0 0 6px 6px',
            background: 'var(--bg-surface-2)',
            fontSize: '0.875rem',
            fontFamily: 'var(--font-mono)',
            lineHeight: '1.65',
          }}
        >
          {current.code}
        </SyntaxHighlighter>
      </CodeBlock>
    </Container>
  );
};

const Container = styled.div`
  padding: 3rem 0 4rem;
  max-width: 860px;
`;

const BackLink = styled(Link)`
  font-family: var(--font-mono);
  font-size: 0.8125rem;
  color: var(--text-muted);
  text-decoration: none;
  display: inline-block;
  margin-bottom: 2.5rem;
  transition: color 0.15s;

  &:hover {
    color: var(--text-primary);
  }
`;

const NotFound = styled.p`
  color: var(--text-muted);
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.625rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.625rem;
  letter-spacing: -0.02em;

  @media (max-width: 600px) {
    font-size: 1.375rem;
  }
`;

const Description = styled.p`
  font-size: 1rem;
  color: var(--text-muted);
  margin: 0 0 1rem;
  line-height: 1.6;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`;

const Tag = styled.span`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border: 1px solid var(--border);
  border-radius: 3px;
  color: var(--text-muted);
`;

const CodeBlock = styled.div`
  border: 1px solid var(--border);
  border-radius: 6px;
  overflow: hidden;
`;

const CodeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem 0 0;
  background: var(--bg-surface);
  border-bottom: 1px solid var(--border);
`;

const Tabs = styled.div`
  display: flex;
`;

const Tab = styled.button`
  font-family: var(--font-mono);
  font-size: 0.75rem;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  border-bottom: 2px solid ${props => props.$active ? 'var(--accent)' : 'transparent'};
  color: ${props => props.$active ? 'var(--text-primary)' : 'var(--text-muted)'};
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
  white-space: nowrap;

  &:hover {
    color: var(--text-primary);
  }
`;

export default SnippetPage;
